/**
 * /privacy and /terms — one component, two documents.
 *
 * Google Play requires a privacy-policy URL that anyone can open without signing in, so this
 * route sits outside the auth gate (router.jsx) and outside apiClient's redirect (PUBLIC_PATHS).
 * Styled with the shared brand CSS so it reads as part of the product rather than a legal annex.
 *
 * The words live in legalConstants.ts and describe only what the code really does.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { EMO_EMAIL } from "../constants";
import { ROUTES } from "../../constants/routes";
import { brandPageCss } from "../brand/brandPageCss";
import { BrandNav, BrandFooter, useBrandTheme } from "../brand/BrandChrome";
import {
  LEGAL_UPDATED,
  PRIVACY_TITLE,
  PRIVACY_INTRO,
  PRIVACY_SECTIONS,
  TERMS_TITLE,
  TERMS_INTRO,
  TERMS_SECTIONS,
  LegalSection,
} from "./legalConstants";

const SCOPE = "cclg";

const LegalPage: React.FC<{ doc: "privacy" | "terms" }> = ({ doc }) => {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useBrandTheme();
  const isPrivacy = doc === "privacy";
  const title = isPrivacy ? PRIVACY_TITLE : TERMS_TITLE;
  const intro = isPrivacy ? PRIVACY_INTRO : TERMS_INTRO;
  const sections: LegalSection[] = isPrivacy ? PRIVACY_SECTIONS : TERMS_SECTIONS;

  return (
    <div className={SCOPE} data-theme={theme}>
      <style>{brandPageCss(SCOPE) + LEGAL_PAGE_CSS}</style>

      <BrandNav theme={theme} onToggleTheme={toggleTheme}>
        <button
          className="linkbtn hide-sm"
          onClick={() => navigate(isPrivacy ? ROUTES.TERMS : ROUTES.PRIVACY)}
        >
          {isPrivacy ? "Terms" : "Privacy"}
        </button>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.CONTACT)}>
          Help
        </button>
      </BrandNav>

      <div className="wrap hero">
        <span className="eyebrow">Updated {LEGAL_UPDATED}</span>
        <h1>{title}</h1>
        <p className="sub">{intro}</p>
      </div>

      <section className="wrap doc">
        {sections.map((s) => (
          <div className="blk" key={s.heading}>
            <h2>{s.heading}</h2>
            {s.paragraphs?.map((p) => (
              <p key={p}>{p}</p>
            ))}
            {s.bullets && (
              <ul>
                {s.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
        <div className="blk">
          <h2>Contact</h2>
          <p>
            <a href={`mailto:${EMO_EMAIL}`}>{EMO_EMAIL}</a>
          </p>
        </div>
      </section>

      <BrandFooter>
        <button className="linkbtn" onClick={() => navigate(isPrivacy ? ROUTES.TERMS : ROUTES.PRIVACY)}>
          {isPrivacy ? "Terms of service" : "Privacy policy"}
        </button>
      </BrandFooter>
    </div>
  );
};

// Shared with the /ai connector guide (connector/ConnectorGuidePage.tsx), same scope class.
export const LEGAL_PAGE_CSS = `
.cclg .hero{padding-top:54px}
.cclg .hero h1{font-size:clamp(32px,5vw,46px);margin-bottom:14px}
.cclg .hero .sub{color:var(--muted);font-size:17px;max-width:60ch;margin:0}
.cclg .doc{display:flex;flex-direction:column;gap:34px;padding-top:44px;padding-bottom:70px}
.cclg .blk{display:flex;flex-direction:column;gap:11px;max-width:64ch}
.cclg .blk h2{font-size:20px}
.cclg .blk p{margin:0;color:var(--muted);font-size:15.5px;line-height:1.62}
.cclg .blk ul{margin:0;padding:0;list-style:none;display:flex;flex-direction:column;gap:10px}
.cclg .blk li{position:relative;padding-left:24px;color:var(--muted);font-size:15.5px;line-height:1.62}
.cclg .blk li::before{content:"";position:absolute;left:5px;top:9px;width:6px;height:6px;border-radius:50%;background:var(--cherry)}
`;

export default LegalPage;
